/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export enum FulfillmentServiceType {
  InStorePickup = 'btg::fulfillment-service::in-store-pickup',
  // https://www.7-11.com.tw/
  SevenEleven = 'btg::fulfillment-service::tw::seven',
  // https://www.e-can.com.tw/
  Ecan = 'btg::fulfillment-service::tw::ecan',
  // 大電原廠配送 (6h)
  MajorApplianceDelivery = 'btg::fulfillment-service::tw::6h::major-appliance-delivery',
  // 三機原廠配送 (6h)
  KitchenApplianceDelivery = 'btg::fulfillment-service::tw::6h::kitchen-appliance-delivery',
  // 西屋電視原廠配送 (6h)
  WestinghouseTVDelivery = 'btg::fulfillment-service::tw::6h::westinghouse-tv-delivery',
  // 農產水果產地直送 (6h)
  FruitDirectShipping = 'btg::fulfillment-service::tw::6h::fruit-direct-shipping',
  // https://www.traveltobuy.com/travelmate (6h)
  TravelToBuyDelivery = 'btg::fulfillment-service::tw::6h::traveltobuy-delivery',
}

export interface FulfillmentServiceData {
  id: string;
  handle: string;
  name: string;
  description: string;
}

const cachedFulfillmentServices = new Map<string, FulfillmentService>();

export class FulfillmentService {
  public static create(data: FulfillmentServiceData): FulfillmentService {
    const service = cachedFulfillmentServices.get(data.id);
    if (service) {
      return service;
    }

    const newService = new FulfillmentService(data);
    cachedFulfillmentServices.set(data.id, newService);
    return newService;
  }

  public readonly type: FulfillmentServiceType;

  private constructor(private readonly data: FulfillmentServiceData) {
    switch (data.handle) {
      case FulfillmentServiceType.InStorePickup:
      case FulfillmentServiceType.SevenEleven:
      case FulfillmentServiceType.Ecan:
      case FulfillmentServiceType.MajorApplianceDelivery:
      case FulfillmentServiceType.KitchenApplianceDelivery:
      case FulfillmentServiceType.WestinghouseTVDelivery:
      case FulfillmentServiceType.FruitDirectShipping:
      case FulfillmentServiceType.TravelToBuyDelivery:
        this.type = data.handle as FulfillmentServiceType;
        break;

      default:
        throw new Error(`unsupported fulfillment service ${data.handle} (${data.name})`);
    }
  }

  get id() { return this.data.id; }
  get handle() { return this.data.handle; }
  get name() { return this.data.name; }
  get description() { return this.data.description; }
}
